import { css } from '@emotion/react';
import {
  faCirclePlus,
  faCog, faFillDrip, faPaintBrush, faPalette, faPen, faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import {
  RoundButtonComponent, RoundButtonSize,
} from '~/_shared/baseComponents/buttons';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import {
  DropdownComponent, DropdownItemComponent,
} from '~/_shared/baseComponents/dropdown';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { InputSize } from '~/_shared/baseComponents/inputs';
import { useTranslation } from '~/_shared/utils/hooks';
import { ActiveBoundaryContextMenuComponent } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/contextMenu/activeBoundaryContextMenu.component';
import {
  toolbarButtonStyle, toolbarButtonWrapperStyle,
} from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/styles';
import type { BoundaryGroupPermissions } from '~/store/boundaryGroups/boundaryGroups.selectors';

const ddItemStyle = css({
  display: 'flex',
  gap: 16,
  width: '100%',
});

type ActiveBoundaryToolbarProps = Readonly<{
  boundaryIsOptimizedTerritoriesTrial: boolean;
  boundaryGroupId: number;
  boundaryPermissions: BoundaryGroupPermissions;

  onAddFillClick: () => void;
  onClone?: () => void;
  onConvert?: () => void;
  onCustomizeFillClick: () => void;
  onCustomizeMetricsClick: () => void;
  onExportClick?: () => void;
  onRemove: () => void;
  onSettingsClick: () => void;
  onStartEditClick?: () => void;
}>;

export const ActiveBoundaryToolbarComponent = ({
  boundaryIsOptimizedTerritoriesTrial,
  boundaryGroupId,
  boundaryPermissions,
  onAddFillClick,
  onClone,
  onConvert,
  onCustomizeFillClick,
  onCustomizeMetricsClick,
  onExportClick,
  onRemove,
  onStartEditClick,
  onSettingsClick,
}: ActiveBoundaryToolbarProps) => {
  const [isFillDropdownOpen, setIsFillDropdownOpen] = useState(false);
  const { boundaryIsCustom } = boundaryPermissions;
  const [t] = useTranslation();

  const getCustomizeFillButtonStyle = () => {
    if (isFillDropdownOpen) {
      return RoundButtonStyle.Quaternary;
    }
    if (boundaryIsOptimizedTerritoriesTrial) {
      return RoundButtonStyle.Restricted;
    }
    return RoundButtonStyle.Primary;
  };

  const getEditButtonStyle = () => {
    if (!onStartEditClick) {
      return RoundButtonStyle.Disabled;
    }

    if (boundaryIsOptimizedTerritoriesTrial) {
      return RoundButtonStyle.Restricted;
    }

    return RoundButtonStyle.Primary;
  };

  const getEditButtonTooltip = () => {
    if (!onStartEditClick) {
      if (boundaryIsCustom && !boundaryPermissions.boundaryIsEditable) {
        return t('The owner of the territory ({{ownerName}}) doesn\'t allow editing of this territory group',
          { ownerName: boundaryPermissions.boundaryOwnerName });
      }

      return t('You cannot edit territories when an alternative fill is active');
    }

    if (boundaryIsCustom) {
      return t('Start Edit Mode');
    }

    return t('Set Fill Manually');
  };

  return (
    <div css={toolbarButtonWrapperStyle}>
      <div css={toolbarButtonStyle}>
        <RoundButtonComponent
          isDisabled={!onStartEditClick}
          buttonStyle={getEditButtonStyle()}
          icon={boundaryIsCustom ? faPen : faPaintBrush}
          onClick={onStartEditClick}
          size={RoundButtonSize.Small}
          tooltipLabel={getEditButtonTooltip()}
        />
      </div>

      {!boundaryIsCustom && (
        <div css={toolbarButtonStyle}>
          <DropdownComponent
            isOpen={isFillDropdownOpen}
            inPortal
            triggerComponent={(
              <RoundButtonComponent
                icon={faFillDrip}
                onClick={() => setIsFillDropdownOpen(true)}
                size={RoundButtonSize.Small}
                tooltipLabel={t('Fill Settings')}
                buttonStyle={getCustomizeFillButtonStyle()}
              />
            )}
            onClose={() => setIsFillDropdownOpen(false)}
          >
            <DropdownItemComponent
              css={ddItemStyle}
              prefix={<FontAwesomeIcon icon={faCirclePlus} />}
              name={t('Add More Fills')}
              size={InputSize.Small}
              onClick={onAddFillClick}
            />
            <DropdownItemComponent
              css={ddItemStyle}
              prefix={<FontAwesomeIcon icon={faPalette} />}
              name={t('Customize Fills')}
              size={InputSize.Small}
              onClick={onCustomizeFillClick}
            />
          </DropdownComponent>
        </div>
      )}

      <div css={toolbarButtonStyle}>
        <RoundButtonComponent
          icon={faCog}
          onClick={onSettingsClick}
          size={RoundButtonSize.Small}
          tooltipLabel={t(boundaryIsCustom ? 'Territory Group Settings' : 'Boundary Settings')}
        />
      </div>

      <div css={toolbarButtonStyle}>
        <RoundButtonComponent
          icon={faXmark}
          onClick={onRemove}
          buttonStyle={RoundButtonStyle.Danger}
          size={RoundButtonSize.Small}
          tooltipLabel={
            t(boundaryIsCustom ?
              'Remove Territory Group from Map' :
              'Remove Boundary from Map')}
        />
      </div>

      <ActiveBoundaryContextMenuComponent
        boundaryGroupId={boundaryGroupId}
        isBoundaryGroupCustom={boundaryIsCustom}
        onConvert={onConvert}
        onClone={onClone}
        onCustomizeMetricsClick={onCustomizeMetricsClick}
        onExportClick={onExportClick}
      />
    </div>
  );
};
